/**
 * Machine Conflict Resolver Modal.
 *
 * Pops when the backend returns `409 MACHINE_CONFLICT` — meaning the operator
 * is trying to clock onto a new machine while still clocked onto a previous one.
 * Forces the user to backdate the clock-off time on the old one and give a reason
 * (e.g. "forgot to clock off yesterday arvo"), then re-tries the original action.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Save, X, Clock } from "lucide-react";
import { Button, Modal, Input, Textarea, Select } from "../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { value: "forgot_to_clock_off", label: "Forgot to clock off" },
  { value: "end_of_shift", label: "End of shift" },
  { value: "switched_machines", label: "Switched to a different machine" },
  { value: "finished_job", label: "Job finished earlier than logged" },
  { value: "admin_correction", label: "Admin correction" },
  { value: "other", label: "Other" },
];

/**
 * Show the modal given an axios 409 error.
 * Returns an instance ready to render.
 *   const { modalProps } = useMachineConflictModal({ onResolved: () => retry() });
 *   modalProps.triggerFromError(err)  ← call inside your catch block
 */
export const MachineConflictModal = ({ conflict, onClose, onResolved }) => {
  const [clockOffTime, setClockOffTime] = useState(
    () => new Date(new Date().getTime() - 60 * 60 * 1000).toISOString().slice(0, 16)
  );
  const [reason, setReason] = useState("forgot_to_clock_off");
  const [customReason, setCustomReason] = useState("");
  const [endingHours, setEndingHours] = useState("");
  const [endingKms, setEndingKms] = useState("");
  const [saving, setSaving] = useState(false);

  if (!conflict) return null;

  const startedAt = conflict.conflict_started_at;
  const startedLabel = startedAt
    ? new Date(startedAt).toLocaleString("en-AU", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  const submit = async () => {
    if (!clockOffTime) {
      toast.error("Set the actual clock-off time, mate");
      return;
    }
    const resolvedReason = reason === "other" ? customReason.trim() : REASONS.find((r) => r.value === reason)?.label;
    if (!resolvedReason) {
      toast.error("Please pick (or type) a reason");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        conflict_kind: conflict.conflict_kind,
        conflict_id: conflict.conflict_id,
        clock_off_time: new Date(clockOffTime).toISOString(),
        reason: resolvedReason,
      };
      if (endingHours) payload.ending_hours = parseFloat(endingHours);
      if (endingKms) payload.ending_kms = parseFloat(endingKms);
      await axios.post(`${API}/machine-conflict/resolve`, payload);
      toast.success("Old machine clocked off — you're good to go.");
      onClose && onClose();
      onResolved && onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resolve conflict");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Already Clocked On — Resolve First" size="md">
      <div className="space-y-4" data-testid="machine-conflict-modal">
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-bold text-amber-900">Can't operate two machines at once, mate.</p>
            <p className="text-sm text-amber-800 mt-1">
              You're still clocked on to{" "}
              <strong>{conflict.conflict_machine_name || "another machine"}</strong>
              {conflict.conflict_client_name ? ` (${conflict.conflict_client_name})` : ""}
              {conflict.conflict_job_title ? ` · ${conflict.conflict_job_title}` : ""}.
            </p>
            <p className="text-xs text-amber-700 mt-1">
              <Clock className="w-3 h-3 inline mr-1" />
              Started {startedLabel}
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold uppercase text-slate-600 mb-1">
            Actual Clock-Off Time *
          </label>
          <input
            type="datetime-local"
            value={clockOffTime}
            onChange={(e) => setClockOffTime(e.target.value)}
            data-testid="conflict-clock-off-time"
            className="w-full h-14 bg-white border-2 border-slate-300 focus:border-orange-500 focus:outline-none rounded-md px-4 text-lg font-mono"
          />
          <p className="text-xs text-slate-500 mt-1">
            When did you actually stop running this machine?
          </p>
        </div>

        <Select
          label="Why wasn't it clocked off then?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          options={REASONS}
          testId="conflict-reason"
        />
        {reason === "other" && (
          <Textarea
            label="Tell us more"
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            placeholder="e.g. Switched cranes halfway through the pour..."
            rows={2}
            testId="conflict-custom-reason"
          />
        )}

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Ending Hours (optional)"
            type="number"
            step="0.1"
            value={endingHours}
            onChange={(e) => setEndingHours(e.target.value)}
            placeholder="e.g. 1510.5"
            testId="conflict-ending-hours"
          />
          <Input
            label="Ending Kms (optional)"
            type="number"
            step="0.1"
            value={endingKms}
            onChange={(e) => setEndingKms(e.target.value)}
            placeholder="e.g. 60200"
            testId="conflict-ending-kms"
          />
        </div>

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="conflict-resolve-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Clock Off & Continue"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

/**
 * Helper — given an axios error, return a conflict payload if it's a 409 MACHINE_CONFLICT,
 * else null. Usage inside catch():
 *   const c = parseMachineConflict(err);
 *   if (c) { setConflict(c); return; }
 */
export const parseMachineConflict = (err) => {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && d.code === "MACHINE_CONFLICT") {
    return d;
  }
  return null;
};

export default MachineConflictModal;
